/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';

import { Box, CircularProgress, Typography, Modal, Button, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AttachFile, Videocam, Edit } from '@material-ui/icons';
import { arrayMoveImmutable } from 'array-move';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { ReactComponent as AiGeneratedTag } from '../../assets/images/ai-generate-tag.svg';
import CaseStatusCell from '../../components/cases/CaseStatusCell';
import BackButton from '../../components/common/BackButton';
import TypeChipDropdown from '../../components/common/TypeChipDropdown';
import EditCategoryModal from '../../components/observation/EditCategoryModal';
import MaterialsList from '../../components/observation/MaterialsList';
import ObservationQuestions from '../../components/observation/ObservationQuestions';
import QualityObservationScoring from '../../components/observation/QualityObservationScoring';
import SelectMaterials from '../../components/selectMaterials/SelectMaterials';
import { ACTIONS } from '../../constants/logs';
import { getCategoryLabel, getSubcategoryLabel } from '../../constants/observationCategories';
import { MODALITY_OPTIONS_LABELS, observationColors, observationStatuses, TYPE_OPTIONS_ENUM } from '../../constants/observations';
import { useQualityObservationSocket } from '../../hooks/useWebSocketEvents';
import LogService from '../../services/log.service';
import QualityEvaluationService from '../../services/quality-evaluation.service';
import QualityObservationService from '../../services/quality-observation.service';
import { launchAppCreatorMode } from '../../utils/resources';

const useStyles = makeStyles(() => ({
  answers: {
    '&.confirmed': {
      backgroundColor: '#DEF9DA',
      borderColor: '#388E3C',
      color: '#388E3C',
    },
    '&.selected': {
      backgroundColor: '#DEF9DA',
      borderColor: '#388E3C',
      color: '#388E3C',
    },
    border: '1px solid #DEE8EF',
    borderRadius: 4,
    color: '#273B4A',
    fontSize: 12,
    padding: '7.5px 10px',
    textAlign: 'left',
  },
  answersGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '460px',
  },
  categoryChip: {
    backgroundColor: '#2AAAE2',
    borderRadius: 16,
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    padding: '4px 8px',
    textTransform: 'uppercase',
  },
  categoryContainer: {
    backgroundColor: '#F4F4F4',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 16,
    padding: '12px 16px',
  },
  categoryLabel: {
    color: '#263238',
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 4,
  },
  categoryTitle: {
    color: '#455a64',
    fontSize: 14,
    fontWeight: 600,
    marginRight: 8,
  },
  examInfoGrid: {
    borderRadius: 5,
    boxShadow: '0px 0px 10px 0px #0000001A',
    display: 'grid',
    gap: 16,
    gridTemplateColumns: '1fr 1fr 1fr',
    marginBottom: 36,
    padding: 20,
  },
  examInfoItem: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    justifyContent: 'flex-start',
  },
  examInfoLabel: {
    color: '#555',
    fontSize: 12,
    fontWeight: 'bold',
  },
  examInfoValue: {
    color: '#999',
    fontSize: 14,
    lineHeight: 1.37,
  },
  label: {
    display: 'flex',
    fontSize: 14,
    fontWeight: 500,
    gap: 8,
    justifyContent: 'flex-start',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '4px',
    left: '50%',
    maxHeight: '90vh',
    maxWidth: '90vw',
    outline: 'none',
    overflow: 'auto',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
  },
  pageTitle: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 18,
    marginBottom: 16,
    marginTop: 16,
  },
  patientName: {
    '& div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    color: '#263238',
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: 24,
    fontWeight: 500,
    maxWidth: '480px',
    textAlign: 'left',
  },
  questionsContainer: {
    borderRadius: 5,
    boxShadow: '0px 0px 10px 0px #0000001A',
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    justifyContent: 'start',
    marginBottom: 30,
    padding: '20px 20px 24px',
  },
  questionsTitle: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'left',
    width: '100%',
  },
  recordInfo: {
    display: 'flex',
    gap: 40,
    marginBottom: 16,
  },
  recordInfoItem: {
    color: '#455a64',
    fontSize: 14,
    fontWeight: 500,
  },
  root: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 46,
    padding: '20px 70px',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 500,
    marginBottom: 10,
    textAlign: 'left',
  },
  subcategoryLabel: {
    color: '#546e7a',
    fontSize: 14,
    fontWeight: 400,
  },
  titleHR: {
    backgroundColor: '#EBEBEB',
    border: 'none',
    height: 1,
    margin: '16px 0',
    width: '100%',
  },
  uncategorizedLabel: {
    color: '#9e9e9e',
    fontSize: 16,
    fontStyle: 'italic',
    fontWeight: 400,
    padding: 12,
  },
  videoPlayer: {
    borderRadius: 16,
    marginTop: 32,
    width: 554,
  },
}));

export default function ObservationDetails() {
  const classes = useStyles();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [observation, setObservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [selectMaterialsOpen, setSelectMaterialsOpen] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);

  useEffect(() => {
    const fetchObservation = async () => {
      try {
        setLoading(true);
        const data = await QualityObservationService.getDetailsById(id);
        setObservation(data);

        if (!data.detailsOpened) {
          await QualityObservationService.update(id, { detailsOpened: true });
        }
      } catch (err) {
        console.error('Error fetching observation:', err);
        enqueueSnackbar('Failed to load observation details', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchObservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEvaluation = async () => {
    if (!id) return;

    try {
      const result = await QualityEvaluationService.getByObservationId(id);

      setEvaluation(result);
    } catch (err) {
      console.error('Error fetching evaluation:', err);
    }
  };

  useEffect(() => {
    fetchEvaluation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useQualityObservationSocket({
    dependencies: [id],
    joinObservationRoom: true,
    observationId: Number(id),
    onCreated: () => {
      fetchEvaluation();
      enqueueSnackbar('New evaluation questions have been generated by AI', {
        autoHideDuration: 8000,
        variant: 'success',
      });
    },
    onFailed: () => {
      fetchEvaluation();
      enqueueSnackbar('AI generated evaluation have failed, demo questions have been used as fallback', {
        autoHideDuration: 8000,
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!id) return;

      try {
        setLoadingMaterials(true);
        const data = await QualityObservationService.getObservationMaterials(id);
        setMaterials(data || []);
      } catch (err) {
        console.error('Error fetching materials:', err);
        enqueueSnackbar('Failed to load materials', { variant: 'error' });
      } finally {
        setLoadingMaterials(false);
      }
    };

    fetchMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleOpenSelectMaterials = () => {
    setSelectMaterialsOpen(true);
  };

  const handleCloseSelectMaterials = () => {
    setSelectMaterialsOpen(false);
  };

  const handleSelectMaterials = async (selectedMaterials) => {
    try {
      await handleAttachMaterials(selectedMaterials);

      handleCloseSelectMaterials();
    } catch (err) {
      console.error('Error processing selected materials:', err);
      enqueueSnackbar('Failed to attach selected materials', { variant: 'error' });
    }
  };

  const handleAttachMaterials = async (selectedMaterials) => {
    if (!selectedMaterials.length) return;

    try {
      const materialIds = selectedMaterials.map((material) => material.id);
      await QualityObservationService.attachMaterials(id, materialIds);

      const updatedMaterials = await QualityObservationService.getObservationMaterials(id);

      setMaterials(updatedMaterials || []);

      enqueueSnackbar('Materials attached successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error attaching materials:', err);
      enqueueSnackbar('Failed to attach materials', { variant: 'error' });
    }
  };

  const handleDetachMaterial = async (materialId) => {
    try {
      await QualityObservationService.detachMaterial(id, materialId);

      setMaterials(materials.filter((material) => material.id !== materialId));

      enqueueSnackbar('Material detached successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error detaching material:', err);
      enqueueSnackbar('Failed to detach material', { variant: 'error' });
    }
  };

  const handleMaterialSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;

    try {
      const newMaterials = arrayMoveImmutable(materials, oldIndex, newIndex);
      setMaterials(newMaterials);

      await QualityObservationService.updateMaterialOrder(
        id,
        newMaterials.map((material) => material.id)
      );
    } catch (err) {
      console.error('Error updating material order:', err);
      enqueueSnackbar('Failed to update material order', { variant: 'error' });
    }
  };

  const handleOpenVideoRecorder = () => {
    new LogService().create({
      action: ACTIONS.RESOURCE_RECORDER_OPENED,
      details: {
        'Observation Id': id,
      },
    });

    launchAppCreatorMode(id);
  };

  const handleTypeChange = async (newType) => {
    try {
      await QualityObservationService.update(id, { type: newType });

      setObservation({ ...observation, type: newType });
      enqueueSnackbar('Observation type updated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error updating observation type:', err);
      enqueueSnackbar('Failed to update observation type', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!observation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6">Observation not found</Typography>
      </Box>
    );
  }

  const ExamInfoItem = ({ label, value }) => (
    <Box className={classes.examInfoItem}>
      <Typography variant="subtitle2" className={classes.examInfoLabel}>
        {label}:
      </Typography>
      <Typography variant="body2" className={classes.examInfoValue}>
        {value}
      </Typography>
    </Box>
  );

  const updateObservationStatus = () => {
    if (observation.status !== observationStatuses.new.value) return;

    setObservation({ ...observation, emailSent: true });
  };

  const handleEditCategory = () => {
    setEditCategoryOpen(true);
  };

  const handleCloseEditCategory = () => {
    setEditCategoryOpen(false);
  };

  const handleUpdateCategory = (updatedObservation) => {
    setObservation(updatedObservation);
  };

  return (
    <div className={classes.root}>
      <Box width={690} display="flex" flexWrap="wrap" flexDirection="column">
        <Box maxWidth={690}>
          <BackButton route="/worklist" goBackHistory />

          <div className={classes.pageTitle}>
            <div className={classes.patientName}>
              <div>{`${observation.patientName} /`}</div> <div>&nbsp;{observation.examName}</div>
            </div>

            <Box display="flex" alignItems="center" gridGap={12}>
              <TypeChipDropdown type={observation.type} onChange={handleTypeChange} disabled={observation.status === observationStatuses.complete.value} />

              <CaseStatusCell
                color={observationColors[observation.status]}
                blackFont={observation.status === observationStatuses.on_hold.value}
                status={observationStatuses[observation.status]?.label}
              />
            </Box>
          </div>

          <div className={classes.recordInfo}>
            <ExamInfoItem label="Record number" value={observation.recordNumber} />
            <ExamInfoItem label="Account number" value={observation.accountNumber} />
          </div>

          <hr className={classes.titleHR} />

          <Box className={classes.examInfoGrid}>
            <ExamInfoItem label="Exam name" value={observation.examName} />

            {/* <ExamInfoItem label="Body part" value={observation.bodyPart} /> */}

            <ExamInfoItem label="Performing Technologist" value={observation.technologist} />

            <ExamInfoItem label="Modality" value={MODALITY_OPTIONS_LABELS[observation.modality] || 'N/A'} />

            <ExamInfoItem label="Location/Facility" value={observation.facility || 'N/A'} />
          </Box>

          {evaluation && evaluation.questions && evaluation.questions.length > 0 && observation.type === TYPE_OPTIONS_ENUM.ROUTINE && (
            <ObservationQuestions
              evaluation={evaluation}
              observationId={id}
              isCompleted={observation.status === observationStatuses.complete.value}
              fetchEvaluation={fetchEvaluation}
            />
          )}
        </Box>

        <Box className={classes.categoryContainer}>
          <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
            <Typography className={classes.categoryTitle}>Issue Category</Typography>
            <AiGeneratedTag />

            <IconButton size="small" title="Modify Category" onClick={handleEditCategory} style={{ marginLeft: 'auto', padding: 4 }}>
              <Edit fontSize="small" />
            </IconButton>
          </Box>
          {observation.category ? (
            <>
              <Typography className={classes.categoryLabel}>{getCategoryLabel(observation.category)}</Typography>
              {observation.subcategory && <Typography className={classes.subcategoryLabel}>{getSubcategoryLabel(observation.subcategory)}</Typography>}
            </>
          ) : (
            <Typography className={classes.uncategorizedLabel}>Uncategorized</Typography>
          )}
        </Box>

        <QualityObservationScoring observation={observation} isCompleted={observation.status === observationStatuses.complete.value} updateStatus={updateObservationStatus} />
      </Box>

      <Box flexDirection="column" width="550px">
        {observation.videoUrl && (
          <video className={classes.videoPlayer} controls src={observation.videoUrl}>
            <track
              kind="captions"
              src={observation.vttSubtitles ? `data:text/vtt;base64,${btoa(observation.vttSubtitles)}` : ''}
              label="English"
              srcLang="en"
              default={!!observation.vttSubtitles}
            />
            Your browser does not support the video tag.
          </video>
        )}

        <Box display="flex" justifyContent="flex-end" mt="16px" gridGap={16}>
          <Button color="primary" variant="outlined" className={classes.actionButton} onClick={handleOpenSelectMaterials} startIcon={<AttachFile />}>
            Attach more
          </Button>

          <Button color="primary" variant="contained" className={classes.actionButton} onClick={handleOpenVideoRecorder} startIcon={<Videocam />}>
            Create Educational Video
          </Button>
        </Box>

        {materials.length > 0 && (
          <Box flexGrow={1} mt={4}>
            <Typography style={{ alignItems: 'center', display: 'flex', fontSize: 20, fontWeight: 'bold', gap: 6, marginBottom: '16px' }}>
              <AttachFile fontSize="small" /> Attached Materials
            </Typography>

            {loadingMaterials ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <MaterialsList materials={materials} onRemove={handleDetachMaterial} sortable onSortEnd={handleMaterialSortEnd} />
            )}
          </Box>
        )}
      </Box>

      <Modal open={selectMaterialsOpen} onClose={handleCloseSelectMaterials} aria-labelledby="select-materials-modal" aria-describedby="select-materials-for-observation">
        <div className={classes.modalContent}>
          <SelectMaterials onSelectMaterials={handleSelectMaterials} onCloseViewer={handleCloseSelectMaterials} preSelectedMaterials={materials} isTechQA />
        </div>
      </Modal>

      {/* Edit Category Modal */}
      <EditCategoryModal open={editCategoryOpen} onClose={handleCloseEditCategory} observation={observation} onUpdate={handleUpdateCategory} />
    </div>
  );
}
